import React, { useState, useEffect } from "react";
import {
    Card,
    Row,
    Col,
    Form,
    Button,
    Spinner,
    Alert,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";

import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

export default function ModifierFournisseur() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        raison_sociale: "",
        adresse: "",
        telephone: "",
        email: "",
        ice: "",
        patente: "",
        rc: "",
        identifiant_fiscal: "",
        cnss: "",
        conditions_reglement: "",
    });

    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState(false);

    useEffect(() => {
        fetchFournisseur();
    }, [id]);

    // ============================================================
    // CHARGEMENT DU FOURNISSEUR
    // ============================================================
    const fetchFournisseur = async () => {
        try {
            setLoading(true);
            setSubmitError("");

            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?id=${id}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    "Impossible de charger les informations du fournisseur."
                );
            }

            const result = await response.json();

            if (!result.success && !result.data) {
                throw new Error(
                    result.message ||
                        "Impossible de charger le fournisseur."
                );
            }

            if (result.data) {
                setFormData({
                    raison_sociale: result.data.raison_sociale || "",
                    adresse: result.data.adresse || "",
                    telephone: result.data.telephone || "",
                    email: result.data.email || "",
                    ice: result.data.ice || "",
                    patente: result.data.patente || "",
                    rc: result.data.rc || "",
                    identifiant_fiscal:
                        result.data.identifiant_fiscal || "",
                    cnss: result.data.cnss || "",
                    conditions_reglement:
                        result.data.conditions_reglement || "",
                    code_fournisseur:
                        result.data.code_fournisseur || "",
                });
            }
        } catch (err) {
            console.error("Erreur chargement fournisseur :", err);
            setSubmitError(
                err.message ||
                    "Une erreur est survenue lors du chargement."
            );
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // MODIFICATION DES CHAMPS
    // ============================================================
    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: null,
            }));
        }

        if (submitError) {
            setSubmitError("");
        }
    };

    // ============================================================
    // VALIDATION
    // ============================================================
    const validateForm = () => {
        const newErrors = {};

        if (!formData.raison_sociale?.trim()) {
            newErrors.raison_sociale =
                "La raison sociale est requise.";
        }

        if (
            formData.email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                formData.email.trim()
            )
        ) {
            newErrors.email = "L'adresse email n'est pas valide.";
        }

        return newErrors;
    };

    // ============================================================
    // ENREGISTREMENT
    // ============================================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = validateForm();

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);

            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });

            return;
        }

        setSaving(true);
        setSubmitError("");
        setSubmitSuccess(false);

        try {
            const token = localStorage.getItem("token");

            const payload = {
                raison_sociale:
                    formData.raison_sociale?.trim() || "",
                adresse: formData.adresse?.trim() || "",
                telephone: formData.telephone?.trim() || "",
                email: formData.email?.trim() || "",
                ice: formData.ice?.trim() || "",
                patente: formData.patente?.trim() || "",
                rc: formData.rc?.trim() || "",
                identifiant_fiscal:
                    formData.identifiant_fiscal?.trim() || "",
                cnss: formData.cnss?.trim() || "",
                conditions_reglement:
                    formData.conditions_reglement?.trim() || "",
            };

            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?id=${id}`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            const result = await response.json();

            if (response.ok && result.success) {
                setSubmitSuccess(true);

                setTimeout(() => {
                    navigate(`/achats/fournisseurs/${id}`);
                }, 1500);
            } else {
                setSubmitError(
                    result.message ||
                        "Erreur lors de la modification du fournisseur."
                );
            }
        } catch (err) {
            console.error("Erreur modification fournisseur :", err);

            setSubmitError(
                "Erreur de connexion au serveur. Veuillez réessayer."
            );
        } finally {
            setSaving(false);
        }
    };

    // ============================================================
    // LOADER
    // ============================================================
    if (loading) {
        return (
            <>
                <Header />

                <div
                    className="main main-app p-3 p-lg-4"
                    style={{
                        background: "#f8fafc",
                        minHeight: "100vh",
                    }}
                >
                    <div
                        className="container-fluid d-flex align-items-center justify-content-center"
                        style={{
                            minHeight: "calc(100vh - 140px)",
                        }}
                    >
                        <div className="text-center">
                            <div className="modern-loader mb-4">
                                <div className="loader-ring loader-ring-1"></div>
                                <div className="loader-ring loader-ring-2"></div>

                                <div className="loader-icon">
                                    <i className="ri-store-2-line"></i>
                                </div>
                            </div>

                            <h5 className="fw-bold mb-1">
                                Chargement du fournisseur
                            </h5>

                            <p className="text-muted mb-0">
                                Récupération des informations...
                            </p>

                            <div className="loading-progress mt-4">
                                <div className="loading-progress-bar"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />
            </>
        );
    }

    // ============================================================
    // PAGE
    // ============================================================
    return (
        <>
            <Header />

            <div
                className="main main-app p-3 p-lg-4"
                style={{
                    background: "#f8fafc",
                    minHeight: "100vh",
                }}
            >
                <div className="container-fluid">

                    {/* ==================================================
                        BREADCRUMB / HEADER
                    ================================================== */}
                    <div className="mb-4">
                        <Button
                            variant="link"
                            className="p-0 text-decoration-none d-inline-flex align-items-center gap-2 text-muted back-link mb-3"
                            onClick={() => navigate(-1)}
                        >
                            <span className="back-icon">
                                <i className="ri-arrow-left-line"></i>
                            </span>
                            Retour au fournisseur
                        </Button>

                        <div className="d-md-flex align-items-center justify-content-between gap-3">
                            <div>
                                <div className="d-flex align-items-center gap-3">
                                    <div
                                        className="page-icon"
                                        style={{
                                            background: `linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary})`,
                                        }}
                                    >
                                        <i className="ri-edit-2-line"></i>
                                    </div>

                                    <div>
                                        <h4 className="main-title mb-1 fw-bold">
                                            Modifier le fournisseur
                                        </h4>

                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <span className="text-muted">
                                                {formData.raison_sociale ||
                                                    "Fournisseur"}
                                            </span>

                                            {formData.code_fournisseur && (
                                                <>
                                                    <span className="text-muted">
                                                        •
                                                    </span>

                                                    <span className="code-badge">
                                                        {
                                                            formData.code_fournisseur
                                                        }
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3 mt-md-0">
                                <Button
                                    variant="light"
                                    onClick={() =>
                                        navigate(
                                            "/achats/fournisseurs"
                                        )
                                    }
                                    className="rounded-pill px-4 d-flex align-items-center gap-2 shadow-sm border"
                                >
                                    <i className="ri-close-line"></i>
                                    Annuler
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* ==================================================
                        ALERTES
                    ================================================== */}
                    {submitSuccess && (
                        <Alert
                            variant="success"
                            dismissible
                            onClose={() =>
                                setSubmitSuccess(false)
                            }
                            className="alert-modern mb-4"
                        >
                            <div className="d-flex align-items-center">
                                <div className="alert-icon success">
                                    <i className="ri-checkbox-circle-line"></i>
                                </div>

                                <div>
                                    <div className="fw-bold">
                                        Modification réussie
                                    </div>

                                    <div className="small">
                                        Le fournisseur a été modifié
                                        avec succès. Redirection...
                                    </div>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {submitError && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => setSubmitError("")}
                            className="alert-modern mb-4"
                        >
                            <div className="d-flex align-items-center">
                                <div className="alert-icon danger">
                                    <i className="ri-error-warning-line"></i>
                                </div>

                                <div>
                                    <div className="fw-bold">
                                        Une erreur est survenue
                                    </div>

                                    <div className="small">
                                        {submitError}
                                    </div>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {/* ==================================================
                        CARTE PRINCIPALE
                    ================================================== */}
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">

                        {/* HEADER DE CARTE */}
                        <div
                            className="edit-card-header"
                            style={{
                                background: `linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary})`,
                            }}
                        >
                            <div className="header-shape header-shape-1"></div>
                            <div className="header-shape header-shape-2"></div>

                            <div className="position-relative d-flex align-items-center gap-3">
                                <div className="header-icon">
                                    <i className="ri-building-2-line"></i>
                                </div>

                                <div>
                                    <h5 className="text-white fw-bold mb-1">
                                        Informations du fournisseur
                                    </h5>

                                    <p className="text-white opacity-75 mb-0 small">
                                        Mettez à jour les informations
                                        générales et administratives.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <Card.Body className="p-3 p-lg-4">

                            <Form onSubmit={handleSubmit}>

                                {/* ==================================================
                                    INFORMATIONS GÉNÉRALES
                                ================================================== */}
                                <section className="form-section">
                                    <div className="section-title">
                                        <div className="section-title-icon">
                                            <i className="ri-information-line"></i>
                                        </div>

                                        <div>
                                            <h6 className="mb-0 fw-bold">
                                                Informations générales
                                            </h6>

                                            <small className="text-muted">
                                                Informations principales
                                                du fournisseur
                                            </small>
                                        </div>
                                    </div>

                                    <Row className="g-4 mt-1">

                                        <Col lg={6}>
                                            <Form.Group>
                                                <Form.Label className="form-label-modern">
                                                    Raison sociale
                                                    <span className="text-danger ms-1">
                                                        *
                                                    </span>
                                                </Form.Label>

                                                <div className="input-wrapper">
                                                    <i className="ri-building-line input-icon"></i>

                                                    <Form.Control
                                                        type="text"
                                                        name="raison_sociale"
                                                        value={
                                                            formData.raison_sociale ||
                                                            ""
                                                        }
                                                        onChange={
                                                            handleChange
                                                        }
                                                        isInvalid={
                                                            !!errors.raison_sociale
                                                        }
                                                        placeholder="Nom ou raison sociale"
                                                        className="form-control-modern"
                                                    />
                                                </div>

                                                {errors.raison_sociale && (
                                                    <div className="field-error">
                                                        <i className="ri-error-warning-line me-1"></i>
                                                        {
                                                            errors.raison_sociale
                                                        }
                                                    </div>
                                                )}
                                            </Form.Group>
                                        </Col>

                                        <Col lg={6}>
                                            <Form.Group>
                                                <Form.Label className="form-label-modern">
                                                    Code fournisseur
                                                </Form.Label>

                                                <div className="input-wrapper">
                                                    <i className="ri-barcode-line input-icon"></i>

                                                    <Form.Control
                                                        type="text"
                                                        value={
                                                            formData.code_fournisseur ||
                                                            ""
                                                        }
                                                        disabled
                                                        readOnly
                                                        className="form-control-modern disabled-field"
                                                    />
                                                </div>

                                                <Form.Text className="text-muted">
                                                    <i className="ri-lock-line me-1"></i>
                                                    Ce code n'est pas
                                                    modifiable.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                    </Row>
                                </section>

                                {/* ==================================================
                                    COORDONNÉES
                                ================================================== */}
                                <section className="form-section mt-4">

                                    <div className="section-title">
                                        <div className="section-title-icon">
                                            <i className="ri-contacts-line"></i>
                                        </div>

                                        <div>
                                            <h6 className="mb-0 fw-bold">
                                                Coordonnées
                                            </h6>

                                            <small className="text-muted">
                                                Moyens de contact du
                                                fournisseur
                                            </small>
                                        </div>
                                    </div>

                                    <Row className="g-4 mt-1">

                                        <Col lg={6}>
                                            <Form.Group>
                                                <Form.Label className="form-label-modern">
                                                    Téléphone
                                                </Form.Label>

                                                <div className="input-wrapper">
                                                    <i className="ri-phone-line input-icon"></i>

                                                    <Form.Control
                                                        type="text"
                                                        name="telephone"
                                                        value={
                                                            formData.telephone ||
                                                            ""
                                                        }
                                                        onChange={
                                                            handleChange
                                                        }
                                                        placeholder="+212 ..."
                                                        className="form-control-modern"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>

                                        <Col lg={6}>
                                            <Form.Group>
                                                <Form.Label className="form-label-modern">
                                                    Email
                                                </Form.Label>

                                                <div className="input-wrapper">
                                                    <i className="ri-mail-line input-icon"></i>

                                                    <Form.Control
                                                        type="email"
                                                        name="email"
                                                        value={
                                                            formData.email ||
                                                            ""
                                                        }
                                                        onChange={
                                                            handleChange
                                                        }
                                                        isInvalid={
                                                            !!errors.email
                                                        }
                                                        placeholder="contact@entreprise.com"
                                                        className="form-control-modern"
                                                    />
                                                </div>

                                                {errors.email && (
                                                    <div className="field-error">
                                                        <i className="ri-error-warning-line me-1"></i>
                                                        {errors.email}
                                                    </div>
                                                )}
                                            </Form.Group>
                                        </Col>

                                        <Col xs={12}>
                                            <Form.Group>
                                                <Form.Label className="form-label-modern">
                                                    Adresse
                                                </Form.Label>

                                                <div className="input-wrapper textarea-wrapper">
                                                    <i className="ri-map-pin-line input-icon textarea-icon"></i>

                                                    <Form.Control
                                                        as="textarea"
                                                        name="adresse"
                                                        value={
                                                            formData.adresse ||
                                                            ""
                                                        }
                                                        onChange={
                                                            handleChange
                                                        }
                                                        rows={3}
                                                        placeholder="Adresse complète du fournisseur"
                                                        className="form-control-modern"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>

                                    </Row>
                                </section>

                                {/* ==================================================
                                    IDENTIFIANTS JURIDIQUES
                                ================================================== */}
                                <section className="form-section mt-4">

                                    <div className="section-title">
                                        <div className="section-title-icon">
                                            <i className="ri-briefcase-4-line"></i>
                                        </div>

                                        <div>
                                            <h6 className="mb-0 fw-bold">
                                                Identifiants juridiques
                                            </h6>

                                            <small className="text-muted">
                                                Informations fiscales et
                                                administratives
                                            </small>
                                        </div>
                                    </div>

                                    <Row className="g-4 mt-1">

                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="form-label-modern">
                                                    ICE
                                                </Form.Label>

                                                <div className="input-wrapper">
                                                    <i className="ri-fingerprint-line input-icon"></i>

                                                    <Form.Control
                                                        type="text"
                                                        name="ice"
                                                        value={
                                                            formData.ice ||
                                                            ""
                                                        }
                                                        onChange={
                                                            handleChange
                                                        }
                                                        placeholder="ICE"
                                                        className="form-control-modern"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="form-label-modern">
                                                    Patente
                                                </Form.Label>

                                                <div className="input-wrapper">
                                                    <i className="ri-file-text-line input-icon"></i>

                                                    <Form.Control
                                                        type="text"
                                                        name="patente"
                                                        value={
                                                            formData.patente ||
                                                            ""
                                                        }
                                                        onChange={
                                                            handleChange
                                                        }
                                                        placeholder="N° patente"
                                                        className="form-control-modern"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="form-label-modern">
                                                    RC
                                                </Form.Label>

                                                <div className="input-wrapper">
                                                    <i className="ri-file-list-3-line input-icon"></i>

                                                    <Form.Control
                                                        type="text"
                                                        name="rc"
                                                        value={
                                                            formData.rc ||
                                                            ""
                                                        }
                                                        onChange={
                                                            handleChange
                                                        }
                                                        placeholder="Registre de commerce"
                                                        className="form-control-modern"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="form-label-modern">
                                                    Identifiant Fiscal (IF)
                                                </Form.Label>

                                                <div className="input-wrapper">
                                                    <i className="ri-government-line input-icon"></i>

                                                    <Form.Control
                                                        type="text"
                                                        name="identifiant_fiscal"
                                                        value={
                                                            formData.identifiant_fiscal ||
                                                            ""
                                                        }
                                                        onChange={
                                                            handleChange
                                                        }
                                                        placeholder="Identifiant fiscal"
                                                        className="form-control-modern"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="form-label-modern">
                                                    CNSS
                                                </Form.Label>

                                                <div className="input-wrapper">
                                                    <i className="ri-shield-check-line input-icon"></i>

                                                    <Form.Control
                                                        type="text"
                                                        name="cnss"
                                                        value={
                                                            formData.cnss ||
                                                            ""
                                                        }
                                                        onChange={
                                                            handleChange
                                                        }
                                                        placeholder="N° CNSS"
                                                        className="form-control-modern"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>

                                    </Row>
                                </section>

                                {/* ==================================================
                                    CONDITIONS DE RÈGLEMENT
                                ================================================== */}
                                <section className="form-section mt-4">

                                    <div className="section-title">
                                        <div className="section-title-icon">
                                            <i className="ri-bank-card-line"></i>
                                        </div>

                                        <div>
                                            <h6 className="mb-0 fw-bold">
                                                Conditions de règlement
                                            </h6>

                                            <small className="text-muted">
                                                Modalités de paiement
                                                convenues
                                            </small>
                                        </div>
                                    </div>

                                    <div className="mt-3">
                                        <Form.Group>
                                            <Form.Label className="form-label-modern">
                                                Conditions
                                            </Form.Label>

                                            <div className="input-wrapper textarea-wrapper">
                                                <i className="ri-money-dollar-circle-line input-icon textarea-icon"></i>

                                                <Form.Control
                                                    as="textarea"
                                                    name="conditions_reglement"
                                                    value={
                                                        formData.conditions_reglement ||
                                                        ""
                                                    }
                                                    onChange={
                                                        handleChange
                                                    }
                                                    rows={4}
                                                    placeholder="Ex : Paiement à 30 jours, escompte de 2%..."
                                                    className="form-control-modern"
                                                />
                                            </div>
                                        </Form.Group>
                                    </div>
                                </section>

                                {/* ==================================================
                                    ACTIONS
                                ================================================== */}
                                <div className="form-actions mt-4 pt-4">

                                    <div className="text-muted small">
                                        <i className="ri-information-line me-1"></i>
                                        Les champs marqués d'un
                                        <span className="text-danger mx-1">
                                            *
                                        </span>
                                        sont obligatoires.
                                    </div>

                                    <div className="d-flex gap-2 flex-wrap justify-content-end">

                                        <Button
                                            type="button"
                                            variant="light"
                                            onClick={() =>
                                                navigate(
                                                    "/achats/fournisseurs"
                                                )
                                            }
                                            disabled={saving}
                                            className="btn-modern btn-cancel"
                                        >
                                            <i className="ri-close-line me-2"></i>
                                            Annuler
                                        </Button>

                                        <Button
                                            type="submit"
                                            disabled={saving}
                                            className="btn-modern btn-save"
                                            style={{
                                                background: `linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary})`,
                                                border: "none",
                                            }}
                                        >
                                            {saving ? (
                                                <>
                                                    <Spinner
                                                        size="sm"
                                                        className="me-2"
                                                    />
                                                    Enregistrement...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-save-3-line me-2"></i>
                                                    Enregistrer les
                                                    modifications
                                                </>
                                            )}
                                        </Button>

                                    </div>
                                </div>

                            </Form>
                        </Card.Body>
                    </Card>
                </div>

                <Footer />
            </div>

            {/* ============================================================
                STYLES
            ============================================================ */}
            <style>{`

                /* ==========================================================
                   GLOBAL
                ========================================================== */

                .main {
                    color: #1e293b;
                }

                .main-title {
                    color: #0f172a;
                    letter-spacing: -0.3px;
                }

                /* ==========================================================
                   HEADER
                ========================================================== */

                .page-icon {
                    width: 52px;
                    height: 52px;
                    min-width: 52px;
                    border-radius: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    font-size: 23px;
                    box-shadow: 0 8px 20px rgba(15, 45, 90, 0.18);
                }

                .back-link {
                    transition: all 0.2s ease;
                }

                .back-link:hover {
                    color: ${COLORS.primary} !important;
                }

                .back-icon {
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 5px rgba(15, 23, 42, 0.05);
                }

                .code-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 4px 10px;
                    border-radius: 50px;
                    background: rgba(33, 172, 205, 0.10);
                    color: ${COLORS.primary};
                    font-size: 12px;
                    font-weight: 600;
                }

                /* ==========================================================
                   CARD HEADER
                ========================================================== */

                .edit-card-header {
                    min-height: 125px;
                    padding: 28px 32px;
                    position: relative;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                }

                .header-shape {
                    position: absolute;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.08);
                    pointer-events: none;
                }

                .header-shape-1 {
                    width: 220px;
                    height: 220px;
                    right: -60px;
                    top: -110px;
                }

                .header-shape-2 {
                    width: 140px;
                    height: 140px;
                    right: 130px;
                    bottom: -100px;
                    background: rgba(255, 255, 255, 0.05);
                }

                .header-icon {
                    width: 58px;
                    height: 58px;
                    min-width: 58px;
                    border-radius: 16px;
                    background: rgba(255, 255, 255, 0.15);
                    border: 1px solid rgba(255, 255, 255, 0.20);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    font-size: 26px;
                    backdrop-filter: blur(4px);
                }

                /* ==========================================================
                   FORM SECTIONS
                ========================================================== */

                .form-section {
                    padding-bottom: 28px;
                    border-bottom: 1px dashed #e2e8f0;
                }

                .form-section:last-child {
                    border-bottom: none;
                    padding-bottom: 0;
                }

                .section-title {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding-bottom: 12px;
                    border-bottom: 2px solid rgba(33, 172, 205, 0.15);
                    position: relative;
                }

                .section-title::after {
                    content: "";
                    position: absolute;
                    left: 0;
                    bottom: -2px;
                    width: 75px;
                    height: 2px;
                    background: ${COLORS.primary};
                    border-radius: 10px;
                }

                .section-title-icon {
                    width: 38px;
                    height: 38px;
                    min-width: 38px;
                    border-radius: 11px;
                    background: rgba(33, 172, 205, 0.10);
                    color: ${COLORS.primary};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                }

                /* ==========================================================
                   FORM LABELS
                ========================================================== */

                .form-label-modern {
                    font-size: 13px;
                    font-weight: 600;
                    color: #334155;
                    margin-bottom: 8px;
                }

                /* ==========================================================
                   INPUTS
                ========================================================== */

                .input-wrapper {
                    position: relative;
                }

                .input-icon {
                    position: absolute;
                    left: 15px;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 3;
                    color: #94a3b8;
                    font-size: 18px;
                    pointer-events: none;
                    transition: color 0.2s ease;
                }

                .textarea-icon {
                    top: 18px;
                    transform: none;
                }

                .form-control-modern {
                    min-height: 46px;
                    border: 1px solid #e2e8f0 !important;
                    border-radius: 12px !important;
                    padding: 10px 14px 10px 45px !important;
                    color: #1e293b;
                    background: #fff;
                    box-shadow: none !important;
                    transition: all 0.2s ease;
                }

                textarea.form-control-modern {
                    min-height: auto;
                    resize: vertical;
                    padding-top: 12px !important;
                }

                .form-control-modern:hover {
                    border-color: #cbd5e1 !important;
                }

                .form-control-modern:focus {
                    border-color: ${COLORS.primary} !important;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.10) !important;
                }

                .input-wrapper:focus-within .input-icon {
                    color: ${COLORS.primary};
                }

                .disabled-field {
                    background: #f8fafc !important;
                    color: #64748b !important;
                    cursor: not-allowed;
                }

                .field-error {
                    color: #dc2626;
                    font-size: 12px;
                    margin-top: 6px;
                }

                /* ==========================================================
                   ALERTS
                ========================================================== */

                .alert-modern {
                    border: none !important;
                    border-radius: 15px !important;
                    padding: 14px 18px !important;
                    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
                }

                .alert-icon {
                    width: 42px;
                    height: 42px;
                    min-width: 42px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    margin-right: 12px;
                }

                .alert-icon.success {
                    background: rgba(25, 135, 84, 0.10);
                    color: #198754;
                }

                .alert-icon.danger {
                    background: rgba(220, 53, 69, 0.10);
                    color: #dc3545;
                }

                /* ==========================================================
                   ACTIONS
                ========================================================== */

                .form-actions {
                    border-top: 1px solid #e2e8f0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                }

                .btn-modern {
                    min-height: 44px;
                    border-radius: 50px !important;
                    padding: 9px 22px !important;
                    font-weight: 600 !important;
                    display: inline-flex !important;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease !important;
                }

                .btn-cancel {
                    border: 1px solid #e2e8f0 !important;
                    color: #475569 !important;
                    background: #fff !important;
                }

                .btn-cancel:hover {
                    background: #f8fafc !important;
                    transform: translateY(-1px);
                }

                .btn-save {
                    color: #fff !important;
                    box-shadow: 0 5px 15px rgba(15, 45, 90, 0.18);
                }

                .btn-save:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(15, 45, 90, 0.25);
                }

                .btn-save:disabled {
                    opacity: 0.75;
                    transform: none;
                }

                /* ==========================================================
                   LOADER
                ========================================================== */

                .modern-loader {
                    width: 78px;
                    height: 78px;
                    margin: 0 auto;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .loader-ring {
                    position: absolute;
                    inset: 0;
                    border-radius: 50%;
                    border: 3px solid transparent;
                }

                .loader-ring-1 {
                    border-top-color: ${COLORS.primary};
                    border-right-color: ${COLORS.primary};
                    animation: spinLoader 1s linear infinite;
                }

                .loader-ring-2 {
                    inset: 9px;
                    border-bottom-color: ${COLORS.primaryDark};
                    border-left-color: ${COLORS.primaryDark};
                    animation: spinLoaderReverse 1.4s linear infinite;
                }

                .loader-icon {
                    width: 38px;
                    height: 38px;
                    border-radius: 11px;
                    background: rgba(33, 172, 205, 0.10);
                    color: ${COLORS.primary};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 19px;
                }

                @keyframes spinLoader {
                    to {
                        transform: rotate(360deg);
                    }
                }

                @keyframes spinLoaderReverse {
                    to {
                        transform: rotate(-360deg);
                    }
                }

                .loading-progress {
                    width: 180px;
                    height: 4px;
                    margin-left: auto;
                    margin-right: auto;
                    overflow: hidden;
                    background: #e2e8f0;
                    border-radius: 20px;
                }

                .loading-progress-bar {
                    width: 45%;
                    height: 100%;
                    border-radius: 20px;
                    background: ${COLORS.primary};
                    animation: loadingProgress 1.4s ease-in-out infinite;
                }

                @keyframes loadingProgress {
                    0% {
                        transform: translateX(-120%);
                    }

                    50% {
                        transform: translateX(100%);
                    }

                    100% {
                        transform: translateX(350%);
                    }
                }

                /* ==========================================================
                   RESPONSIVE
                ========================================================== */

                @media (max-width: 767.98px) {

                    .edit-card-header {
                        min-height: 110px;
                        padding: 22px 20px;
                    }

                    .header-icon {
                        width: 48px;
                        height: 48px;
                        min-width: 48px;
                        font-size: 21px;
                    }

                    .form-actions {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .form-actions > div:last-child {
                        width: 100%;
                    }

                    .form-actions .btn-modern {
                        flex: 1;
                    }

                    .page-icon {
                        width: 46px;
                        height: 46px;
                        min-width: 46px;
                        font-size: 20px;
                    }
                }

                @media (max-width: 575.98px) {

                    .main-title {
                        font-size: 1.1rem;
                    }

                    .edit-card-header {
                        padding: 20px 16px;
                    }

                    .header-icon {
                        width: 44px;
                        height: 44px;
                        min-width: 44px;
                        border-radius: 13px;
                        font-size: 19px;
                    }

                    .section-title-icon {
                        width: 34px;
                        height: 34px;
                        min-width: 34px;
                        font-size: 16px;
                    }

                    .btn-modern {
                        width: 100%;
                    }

                    .form-actions .d-flex {
                        flex-direction: column;
                    }
                }

            `}</style>
        </>
    );
}